import { useCallback, useEffect, useState } from "react";
import { getTagPreferences, setTagPreferences } from "@/utils/localStore";
const TAG_PREFS_KEY = "pc_tagPreferences";

/**
 * Cross-page reactive hook for shared tag preferences.
 *
 * Reads from localStorage on mount, listens for storage events (other tabs or
 * manual dispatches), and exposes a `toggle` helper that persists instantly.
 */
export function useTagPreferences() {
  const [prefs, setPrefs] = useState(getTagPreferences);

  // Sync from other tabs / windows and same-tab changes
  useEffect(() => {
    const refresh = () => setPrefs(getTagPreferences());
    const onStorage = e => {
      if (e.key === TAG_PREFS_KEY) {
        refresh();
      }
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("pc-tag-preferences-changed", refresh);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("pc-tag-preferences-changed", refresh);
    };
  }, []);

  /** Toggle a single tag and persist. */
  const toggle = useCallback((category, tag) => {
    // Compute from the persisted store and pass a plain value to
    // setPrefs — NOT an updater with the persistence side effect
    // inside. React re-runs updaters, and setTagPreferences's own
    // change event re-syncs every hook instance in between, so an
    // impure updater sees the already-toggled state on its second
    // run and silently toggles the tag straight back off.
    const current = getTagPreferences();
    const arr = [...current[category]];
    const idx = arr.indexOf(tag);
    if (idx >= 0) arr.splice(idx, 1);else arr.push(tag);
    const next = {
      ...current,
      [category]: arr
    };
    setTagPreferences(next);
    setPrefs(next);
  }, []);

  /** Replace all prefs at once (e.g. from profile save). */
  const replaceAll = useCallback(next => {
    setTagPreferences(next);
    setPrefs(next);
  }, []);
  return {
    prefs,
    toggle,
    replaceAll
  };
}